// ============================================
// FAQ 富文本编辑器（从 zlcggb-home TutorialEditor 提取）
// 独立的 Tiptap 编辑器组件，不含页面壳
// ============================================

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Image as ImageIcon, Link2, Code,
  Bold, Italic, Strikethrough, List, ListOrdered, Quote, Heading1,
  Heading2, Heading3, Minus, X, Loader2, Upload,
  Copy, Check, Terminal, Table, GitFork,
  Trash2, ArrowDown as ArrowDownIcon, ArrowUp as ArrowUpIcon,
  ArrowLeft as ArrowLeftIcon, ArrowRight as ArrowRightIcon,
  Pencil, Unlink, ExternalLink,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { Mark, Extension, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import ImageExtension from '@tiptap/extension-image';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import OrderedList from '@tiptap/extension-ordered-list';
import { Table as TableExtension, TableRow, TableHeader, TableCell } from '@tiptap/extension-table';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

// ── 自定义 Extensions（从 TutorialEditor 原样复制） ──

const CustomUnderline = Mark.create({
  name: 'underline',
  parseHTML() {
    return [
      { tag: 'u' },
      {
        style: 'text-decoration',
        consuming: false,
        getAttrs: value => (value as string).includes('underline') ? {} : false,
      },
    ];
  },
  renderHTML({ HTMLAttributes }) {
    return ['u', mergeAttributes(HTMLAttributes), 0];
  },
  addKeyboardShortcuts() {
    return {
      'Mod-u': () => this.editor.commands.toggleMark(this.name),
      'Mod-U': () => this.editor.commands.toggleMark(this.name),
    };
  },
});

const CustomHighlight = Mark.create({
  name: 'highlight',
  addAttributes() {
    return {
      color: {
        default: '#ffe066',
        parseHTML: element => element.getAttribute('data-color') || element.style.backgroundColor,
        renderHTML: attributes => {
          if (!attributes.color) return {};
          return {
            style: `background-color: ${attributes.color}; border-radius: 4px; padding: 2px 4px;`,
            'data-color': attributes.color,
          };
        },
      },
    };
  },
  parseHTML() { return [{ tag: 'mark' }]; },
  renderHTML({ HTMLAttributes }) { return ['mark', mergeAttributes(HTMLAttributes), 0]; },
});

// ── 链接边界插件：mousedown 拦截 + 零宽装饰 ──
// 核心：必须在 mousedown 阶段（浏览器渲染光标之前）拦截并手动设置 selection
// handleClick 太晚（光标已落点），handleDOMEvents.mousedown 是正确时机
const LinkBoundary = Extension.create({
  name: 'linkBoundary',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('linkBoundary'),
        props: {
          // 用 handleDOMEvents 在 mousedown 阶段拦截，此时浏览器还未確定光标位置
          handleDOMEvents: {
            mousedown: (view, event) => {
              const me = event as MouseEvent;
              const target = me.target as HTMLElement;
              const linkEl = target.tagName === 'A' ? target : target.closest('a');
              if (!linkEl) return false;

              const linkRect = linkEl.getBoundingClientRect();
              // 只有点击在链接左侧 35% 区域或最多 32px 内才介入
              const THRESHOLD_PX = Math.min(linkRect.width * 0.35, 32);
              if (me.clientX > linkRect.left + THRESHOLD_PX) return false;

              const posData = view.posAtCoords({ left: me.clientX, top: me.clientY });
              if (!posData) return false;

              const { state } = view;
              const $pos = state.doc.resolve(posData.pos);
              const linkType = state.schema.marks.link;
              const parent = $pos.parent;
              const parentStart = $pos.start();

              // 一次遍历：追踪当前链接段落的起始，当覆盖 posData.pos 时锁定
              let runStart = -1;
              let foundLinkStart = -1;

              parent.forEach((child, offset) => {
                const absPos = parentStart + offset;
                const hasLink = child.marks.some((m: any) => m.type === linkType);

                if (hasLink && runStart === -1) runStart = absPos;       // 链接段开始
                if (!hasLink) runStart = -1;                             // 链接段结束

                // 当前 child 包含点击位置
                if (hasLink && runStart >= 0 && absPos <= posData.pos && posData.pos < absPos + child.nodeSize) {
                  foundLinkStart = runStart;
                }
              });

              if (foundLinkStart >= 0) {
                event.preventDefault();
                view.dispatch(state.tr.setSelection(TextSelection.create(state.doc, foundLinkStart)));
                view.focus();
                return true;
              }
              return false;
            }
          },

          // 装饰：链接左侧插入一个很细的透明占位元素，扭大可点击区域
          decorations: (state) => {
            const { doc, schema } = state;
            const linkType = schema.marks.link;
            if (!linkType) return DecorationSet.empty;

            const decorations: Decoration[] = [];

            doc.descendants((node, pos) => {
              if (!node.isTextblock) return;

              let prevHadLink = false;
              node.forEach((child, offset) => {
                const childPos = pos + 1 + offset;
                const hasLink = child.marks.some((m: any) => m.type === linkType);

                if (hasLink && !prevHadLink) {
                  decorations.push(
                    Decoration.widget(childPos, () => {
                      const span = document.createElement('span');
                      span.setAttribute('data-link-guard', 'before');
                      // 显示一个 12px 宽的透明区域，让用户更容易点到
                      span.style.cssText = 'display:inline-block;width:12px;cursor:text;';
                      return span;
                    }, { side: -1, marks: [] })
                  );
                }

                prevHadLink = hasLink;
              });

              return false;
            });

            return DecorationSet.create(doc, decorations);
          }
        }
      })
    ];
  }
});

const CustomAlign = Extension.create({
  name: 'customAlign',
  addGlobalAttributes() {
    return [{
      types: ['heading', 'paragraph'],
      attributes: {
        align: {
          default: null,
          parseHTML: element => element.style.textAlign || null,
          renderHTML: attributes => {
            if (!attributes.align) return {};
            return { style: `text-align: ${attributes.align}` };
          },
        },
      },
    }];
  },
});

// ── 自定义有序列表：直接扩展节点 schema，支持 list-style-type 属性 ──
const CustomOrderedList = OrderedList.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      listStyleType: {
        default: 'decimal',
        parseHTML: (element: HTMLElement) => {
          return element.getAttribute('data-list-style') || 'decimal';
        },
        renderHTML: (attributes: Record<string, any>) => {
          const s = attributes.listStyleType;
          if (!s || s === 'decimal') return {};
          // 只输出 data 属性，CSS 用选择器匹配，不依赖 list-style-type 自定义值
          return { 'data-list-style': s };
        },
      },
    };
  },
});



function TBtn({ active, onClick, title, children }: {
  active: boolean; onClick: () => void; title: string; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick} title={title}
      className={`w-7 h-7 flex items-center justify-center rounded-md transition-all ${
        active ? 'bg-sky-500 text-white' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
      }`}
    >{children}</button>
  );
}

function Sep() {
  return <div className="w-px h-5 bg-slate-200 mx-0.5" />;
}

// ── 主组件 Props ──

interface FaqRichEditorProps {
  content?: string;
  onChange?: (json: string) => void;
  placeholder?: string;
  minHeight?: string;
  uploadImage: (file: File) => Promise<string>;
  readOnly?: boolean;
  onEditorReady?: (editor: any) => void;
  onFocus?: (editor: any) => void;
  onUploadTask?: (task: Promise<void>) => void;
}

export default function FaqRichEditor({
  content,
  onChange,
  placeholder = '输入内容，或按 / 调出命令菜单...',
  minHeight = '200px',
  uploadImage,
  readOnly = false,
  onEditorReady,
  onFocus,
  onUploadTask,
}: FaqRichEditorProps) {
  // UI 状态
  const [uploadingImageCount, setUploadingImageCount] = useState(0);
  const [hoveredPre, setHoveredPre] = useState<HTMLPreElement | null>(null);
  const [copiedPre, setCopiedPre] = useState<HTMLPreElement | null>(null);
  const [copyBtnStyle, setCopyBtnStyle] = useState<React.CSSProperties>({ display: 'none' });
  const [activeSubMenu, setActiveSubMenu] = useState<'none' | 'title' | 'color'>('none');
  const [showPlus, setShowPlus] = useState(false);
  const [plusPos, setPlusPos] = useState({ top: 0, left: 0 });
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [plusMenuPos, setPlusMenuPos] = useState({ top: 0, left: 0 });
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrlInput, setLinkUrlInput] = useState('');
  const [linkTextInput, setLinkTextInput] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');

  // 链接悬浮条状态与 refs
  const [linkBubble, setLinkBubble] = useState<{ url: string; text: string; top: number; left: number } | null>(null);
  const currentLinkEl = useRef<HTMLAnchorElement | null>(null);
  const [linkEditRange, setLinkEditRange] = useState<{ from: number; to: number } | null>(null);

  const editorRef = useRef<HTMLDivElement>(null);

  // Refs 解决闭包问题
  const showPlusMenuRef = useRef(showPlusMenu);
  showPlusMenuRef.current = showPlusMenu;
  const editorInstanceRef = useRef<ReturnType<typeof useEditor>>(null);
  const loadedContentRef = useRef<string | null>(null);
  const uploadingImage = uploadingImageCount > 0;

  const preloadImage = useCallback((src: string) => {
    return new Promise<void>((resolve) => {
      const img = new window.Image();
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        resolve();
      };
      const timer = window.setTimeout(finish, 8000);
      img.onload = finish;
      img.onerror = finish;
      img.src = src;
      if (img.complete) finish();
    });
  }, []);

  // 图片上传（乐观插入 blob → 后台上传 → 替换 URL）
  const handleEditorImage = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    const ed = editorInstanceRef.current;
    if (!ed) return;

    const blobUrl = URL.createObjectURL(file);
    ed.chain().focus().setImage({ src: blobUrl }).run();

    const uploadTask = (async () => {
      setUploadingImageCount((count) => count + 1);
      try {
        const realUrl = await uploadImage(file);
        await preloadImage(realUrl);
        if ((ed as any).isDestroyed) return;

        const { state } = ed;
        const { tr } = state;
        state.doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs.src === blobUrl) {
            tr.setNodeMarkup(pos, undefined, { ...node.attrs, src: realUrl });
          }
        });
        if (tr.docChanged) {
          ed.view.dispatch(tr);
          onChange?.(JSON.stringify(ed.getJSON()));
        }
      } catch {
        if ((ed as any).isDestroyed) return;

        const { state } = ed;
        const { tr } = state;
        let deletePos = -1;
        state.doc.descendants((node, pos) => {
          if (node.type.name === 'image' && node.attrs.src === blobUrl) {
            deletePos = pos;
          }
        });
        if (deletePos >= 0) {
          const node = state.doc.nodeAt(deletePos);
          if (node) tr.delete(deletePos, deletePos + node.nodeSize);
          if (tr.docChanged) {
            ed.view.dispatch(tr);
            onChange?.(JSON.stringify(ed.getJSON()));
          }
        }
      } finally {
        window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        setUploadingImageCount((count) => Math.max(0, count - 1));
      }
    })();

    onUploadTask?.(uploadTask);
    await uploadTask;
  }, [uploadImage, preloadImage, onChange, onUploadTask]);

  // 触发图片文件选择
  function triggerImageUpload() {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) handleEditorImage(file);
    };
    input.click();
  }


  // 关闭链接弹窗并清空状态
  const closeLinkModal = useCallback(() => {
    setLinkModalOpen(false);
    setLinkUrlInput('');
    setLinkTextInput('');
    setLinkEditRange(null);
  }, []);

  // 链接插入/编辑入口
  const handleInsertLink = useCallback(() => {
    const ed = editorInstanceRef.current;
    if (!ed) return;
    const previousUrl = ed.getAttributes('link').href;
    setLinkUrlInput(previousUrl || '');
    setLinkTextInput('');
    setLinkEditRange(null);
    setLinkModalOpen(true);
  }, []);

  // 应用/保存超链接修改
  const applyLink = useCallback(() => {
    const ed = editorInstanceRef.current;
    if (!ed) return;
    if (linkUrlInput.trim()) {
      const url = linkUrlInput.trim();
      const text = linkTextInput.trim() || url;
      if (linkEditRange) {
        // 编辑模式：选中原文字，删除后插入带超链接的新文字
        ed.chain().focus()
          .setTextSelection(linkEditRange)
          .deleteSelection()
          .insertContent({
            type: 'text',
            text,
            marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }]
          })
          .run();
      } else if (ed.state.selection.empty) {
        // 空白选区插入新超链接
        ed.chain().focus()
          .insertContent({
            type: 'text',
            text,
            marks: [{ type: 'link', attrs: { href: url, target: '_blank' } }]
          })
          .run();
      } else {
        // 非空选区，直接将选中文字设为超链接
        ed.chain().focus().setLink({ href: url }).run();
      }
    }
    closeLinkModal();
  }, [linkUrlInput, linkTextInput, linkEditRange, closeLinkModal]);

  // ── Tiptap 初始化 ──
  const editor = useEditor({
    editable: !readOnly,
    extensions: [
      StarterKit.configure({ link: false, orderedList: false, underline: false }),
      ImageExtension.configure({ allowBase64: false }),
      LinkExtension.extend({ inclusive: false, exitable: true }).configure({ openOnClick: false, autolink: true, linkOnPaste: true }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          if (node.type.name === 'heading') return '标题';
          return placeholder;
        },
      }),
      TableExtension.configure({ resizable: true, handleWidth: 5, cellMinWidth: 80 }),
      TableRow, TableHeader, TableCell,
      CustomUnderline, CustomHighlight, CustomAlign, LinkBoundary, CustomOrderedList,
    ],
    editorProps: {
      attributes: { class: `outline-none pb-8`, style: `min-height: ${minHeight}` },
      handlePaste: (_view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of Array.from(items)) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) handleEditorImage(file);
            return true;
          }
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        for (const file of Array.from(files)) {
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            handleEditorImage(file);
            return true;
          }
        }
        return false;
      },
      handleKeyDown: (_view, event) => {
        if (event.key === 'Escape') {
          if (showPlusMenuRef.current) { setShowPlusMenu(false); event.preventDefault(); return true; }
        }
      },
    },
    content: '',
    onUpdate: ({ editor: e }) => {
      const nextJson = JSON.stringify(e.getJSON());
      if (!readOnly && loadedContentRef.current === null) {
        loadedContentRef.current = nextJson;
      }
      onChange?.(nextJson);
    },
    onFocus: ({ editor: e }) => {
      onFocus?.(e);
    },
  });

  editorInstanceRef.current = editor;

  // 通知父组件 editor 已就绪，挂载全局控制句柄
  useEffect(() => {
    if (editor) {
      (editor as any).triggerInsertLink = handleInsertLink;
      (editor as any).triggerImageUpload = triggerImageUpload;
      if (onEditorReady) onEditorReady(editor);
    }
  }, [editor, onEditorReady, handleInsertLink]);

  // 编辑态只加载初始值；只读预览随外部保存/实时同步原地更新。
  useEffect(() => {
    // 组件卸载时重置
    return () => { loadedContentRef.current = null; };
  }, []);

  useEffect(() => {
    if (!editor) return;
    const nextContent = content || '';
    if (loadedContentRef.current === nextContent) return;
    if (!readOnly && (loadedContentRef.current !== null || !nextContent)) return;

    loadedContentRef.current = nextContent;
    try {
      const parsed = nextContent ? JSON.parse(nextContent) : { type: 'doc', content: [{ type: 'paragraph' }] };
      editor.commands.setContent(parsed, false);
    } catch {
      editor.commands.setContent(nextContent, false);
    }
  }, [editor, content, readOnly]);

  // 选区更新关闭子菜单
  useEffect(() => {
    if (!editor) return;
    const handler = () => {
      setActiveSubMenu('none');
    };
    editor.on('selectionUpdate', handler);
    return () => { editor.off('selectionUpdate', handler); };
  }, [editor]);

  // Slash 检测 — 统一使用加号菜单
  const checkSlashQuery = useCallback(() => {
    if (!editor) return;
    const { state } = editor;
    const { selection } = state;
    const { $from } = selection;
    if (!selection.empty) return;
    const textBefore = $from.parent.textBetween(0, $from.parentOffset, null, '\n');
    // 检测末尾的 /
    if (textBefore.match(/(?:^|\s)\/$/)) {
      // 删掉 / 字符
      const { from } = selection;
      editor.chain().focus().deleteRange({ from: from - 1, to: from }).run();
      // 打开加号菜单（定位到光标位置）
      requestAnimationFrame(() => {
        const sel = window.getSelection();
        if (sel && sel.rangeCount > 0) {
          const range = sel.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          const containerRect = editorRef.current?.getBoundingClientRect();
          if (containerRect) {
            setPlusMenuPos({ top: rect.bottom - containerRect.top + 4, left: rect.left - containerRect.left });
            setShowPlusMenu(true);
          }
        }
      });
    }
  }, [editor]);

  // 加号按钮
  const checkPlusButton = useCallback(() => {
    if (!editor) { setShowPlus(false); setShowPlusMenu(false); return; }
    const { selection } = editor.state;
    if (!selection.empty) { setShowPlus(false); setShowPlusMenu(false); return; }
    const { $from } = selection;
    const isParagraph = $from.parent.type.name === 'paragraph';
    const isEmpty = $from.parent.textContent === '';
    if (isParagraph && isEmpty) {
      const nodeDOM = editor.view.nodeDOM($from.before()) as HTMLElement;
      if (nodeDOM && editorRef.current) {
        const editorRect = editorRef.current.getBoundingClientRect();
        const nodeRect = nodeDOM.getBoundingClientRect();
        setPlusPos({ top: nodeRect.top - editorRect.top + (nodeRect.height - 24) / 2, left: -32 });
        setShowPlus(true);
      } else { setShowPlus(false); setShowPlusMenu(false); }
    } else { setShowPlus(false); setShowPlusMenu(false); }
  }, [editor]);

  // 绑定事件
  useEffect(() => {
    if (!editor) return;
    const handler = () => { checkSlashQuery(); checkPlusButton(); };
    editor.on('update', handler);
    editor.on('selectionUpdate', handler);
    editor.on('focus', handler);
    return () => { editor.off('update', handler); editor.off('selectionUpdate', handler); editor.off('focus', handler); };
  }, [editor, checkSlashQuery, checkPlusButton]);

  // ── 飞书式链接悬浮条：鼠标悬停 0.5 秒延迟触发 ──
  useEffect(() => {
    if (!editor || readOnly) return;
    const proseDom = editorRef.current?.querySelector('.ProseMirror') as HTMLElement | null;
    if (!proseDom) return;

    let hoverTimer: ReturnType<typeof setTimeout> | null = null;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const handleMouseOver = (e: Event) => {
      const target = (e as MouseEvent).target as HTMLElement;
      const linkEl = target.tagName === 'A' ? target : target.closest('a');

      if (linkEl && proseDom.contains(linkEl)) {
        const href = linkEl.getAttribute('href');
        if (!href) return;

        if (hoverTimer) clearTimeout(hoverTimer);
        if (hideTimer) clearTimeout(hideTimer);

        // 0.5 秒后显示悬浮条
        hoverTimer = setTimeout(() => {
          if (editorRef.current) {
            const editorRect = editorRef.current.getBoundingClientRect();
            const linkRect = linkEl.getBoundingClientRect();
            currentLinkEl.current = linkEl as HTMLAnchorElement;
            setLinkBubble({
              url: href,
              text: linkEl.textContent || '',
              top: linkRect.bottom - editorRect.top + 6,
              left: linkRect.left - editorRect.left,
            });
          }
        }, 500);
      }
    };

    const handleMouseOut = (e: Event) => {
      const me = e as MouseEvent;
      const target = me.target as HTMLElement;
      const relatedTarget = me.relatedTarget as HTMLElement | null;

      if (relatedTarget?.closest('.link-bubble-bar')) return;

      const linkEl = target.tagName === 'A' ? target : target.closest('a');
      if (linkEl) {
        if (hoverTimer) {
          clearTimeout(hoverTimer);
          hoverTimer = null;
        }
        hideTimer = setTimeout(() => {
          const bubbleEl = document.querySelector('.link-bubble-bar');
          if (bubbleEl && bubbleEl.matches(':hover')) return;
          setLinkBubble(null);
        }, 300);
      }
    };

    proseDom.addEventListener('mouseover', handleMouseOver);
    proseDom.addEventListener('mouseout', handleMouseOut);

    return () => {
      proseDom.removeEventListener('mouseover', handleMouseOver);
      proseDom.removeEventListener('mouseout', handleMouseOut);
      if (hoverTimer) clearTimeout(hoverTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, [editor, readOnly]);

  // 代码块悬浮复制
  useEffect(() => {
    const container = editorRef.current;
    if (!container) return;
    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const pre = target.closest('pre');
      if (pre && container.contains(pre)) setHoveredPre(pre);
      else setHoveredPre(null);
    };
    const handleMouseLeave = () => setHoveredPre(null);
    container.addEventListener('mouseover', handleMouseOver);
    container.addEventListener('mouseleave', handleMouseLeave);
    return () => { container.removeEventListener('mouseover', handleMouseOver); container.removeEventListener('mouseleave', handleMouseLeave); };
  }, [editor]);

  useEffect(() => {
    if (!hoveredPre || !editorRef.current) { setCopyBtnStyle({ display: 'none' }); return; }
    const preRect = hoveredPre.getBoundingClientRect();
    const containerRect = editorRef.current.getBoundingClientRect();
    setCopyBtnStyle({ position: 'absolute', top: preRect.top - containerRect.top + 8, right: containerRect.right - preRect.right + 12, zIndex: 10 });
  }, [hoveredPre]);

  const handleCopyCode = useCallback(async (pre: HTMLPreElement) => {
    const codeEl = pre.querySelector('code');
    if (!codeEl) return;
    try {
      await navigator.clipboard.writeText(codeEl.textContent || '');
      setCopiedPre(pre);
      setTimeout(() => setCopiedPre(null), 2000);
    } catch { /* ignore */ }
  }, []);

  // 点击外部关闭
  useEffect(() => {
    if (showPlusMenu) {
      const handler = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.editor-plus-btn') && !target.closest('.editor-plus-menu')) setShowPlusMenu(false);
      };
      setTimeout(() => document.addEventListener('click', handler), 0);
      return () => document.removeEventListener('click', handler);
    }
  }, [showPlusMenu]);

  // 段落类型标签
  const getCurrentBlockTypeLabel = () => {
    if (!editor) return '正文';
    if (editor.isActive('heading', { level: 1 })) return '一级标题';
    if (editor.isActive('heading', { level: 2 })) return '二级标题';
    if (editor.isActive('heading', { level: 3 })) return '三级标题';
    return '正文';
  };

  const blockTypes = [
    { label: '正文', active: () => editor?.isActive('paragraph') && !editor?.isActive('heading'), action: () => editor?.chain().focus().setParagraph().run() },
    { label: '一级标题', active: () => editor?.isActive('heading', { level: 1 }), action: () => editor?.chain().focus().toggleHeading({ level: 1 }).run() },
    { label: '二级标题', active: () => editor?.isActive('heading', { level: 2 }), action: () => editor?.chain().focus().toggleHeading({ level: 2 }).run() },
    { label: '三级标题', active: () => editor?.isActive('heading', { level: 3 }), action: () => editor?.chain().focus().toggleHeading({ level: 3 }).run() },
  ];

  // 点击下方大片空白区域自动聚焦并建行
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    if (readOnly || !editor) return;
    const target = e.target as HTMLElement;
    if (target.classList.contains('prose') || target === editorRef.current) {
      const { state } = editor;
      const { doc } = state;
      const lastNode = doc.lastChild;
      const isLastNodeEmptyParagraph = lastNode && lastNode.type.name === 'paragraph' && lastNode.content.size === 0;

      if (!isLastNodeEmptyParagraph) {
        editor.chain().focus().insertContentAt(doc.content.size, { type: 'paragraph' }).run();
      } else {
        editor.chain().focus().setTextSelection(doc.content.size).run();
      }
    }
  }, [editor, readOnly]);

  if (!editor) return null;

  // ── 渲染 ──
  return (
    <div className="relative" ref={editorRef} onClick={handleContainerClick}>
      {/* 图片上传中 */}
      {uploadingImage && (
        <div className="flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-600 text-sm rounded-xl mb-3">
          <Loader2 size={14} className="animate-spin" />
          图片正在后台上传，预览保持显示{uploadingImageCount > 1 ? ` (${uploadingImageCount})` : ''}
        </div>
      )}

      {/* BubbleMenu */}
      {!readOnly && (
        <BubbleMenu editor={editor}>
          <div className="bg-white border border-slate-200 rounded-xl p-1.5 flex items-center gap-1 shadow-lg backdrop-blur-md select-none">
            {/* 标题类型 */}
            <div className="relative">
              <button
                onClick={() => setActiveSubMenu(activeSubMenu === 'title' ? 'none' : 'title')}
                className={`px-2 py-1 flex items-center gap-1 rounded-lg text-xs font-semibold transition-all ${
                  activeSubMenu === 'title' ? 'bg-sky-50 text-sky-600' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <span>{getCurrentBlockTypeLabel()}</span>
                <span className="text-[9px] opacity-60">▼</span>
              </button>
              {activeSubMenu === 'title' && (
                <div className="absolute left-0 top-full mt-2 w-32 bg-white border border-slate-200 rounded-xl py-1 shadow-lg z-50">
                  {blockTypes.map((type, i) => (
                    <button
                      key={i}
                      onClick={() => { type.action(); setActiveSubMenu('none'); }}
                      className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                        type.active() ? 'bg-sky-50 text-sky-600 font-medium' : 'text-slate-500 hover:bg-slate-50'
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Sep />

            {/* 格式按钮 */}
            <TBtn active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="加粗 ⌘B">
              <Bold size={14} />
            </TBtn>
            <TBtn active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="斜体 ⌘I">
              <Italic size={14} />
            </TBtn>
            <TBtn active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleMark('underline').run()} title="下划线 ⌘U">
              <span className="underline font-bold text-xs font-serif leading-none">U</span>
            </TBtn>
            <TBtn active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} title="删除线">
              <Strikethrough size={14} />
            </TBtn>
            <Sep />

            {/* 高亮 */}
            <div className="relative">
              <button
                onClick={() => setActiveSubMenu(activeSubMenu === 'color' ? 'none' : 'color')}
                className={`p-1.5 rounded-lg transition-all ${
                  editor.isActive('highlight') ? 'bg-amber-100 text-amber-600' : 'text-slate-400 hover:bg-slate-100'
                }`}
                title="背景高亮"
              >
                <span className="font-bold text-xs bg-amber-200/80 px-1 rounded border border-amber-300/30">A</span>
              </button>
              {activeSubMenu === 'color' && (
                <div className="absolute left-0 top-full mt-2 p-2 w-40 bg-white border border-slate-200 rounded-xl shadow-lg z-50">
                  <div className="grid grid-cols-5 gap-1.5 justify-items-center">
                    {[
                      { color: '#ffe066', label: '柠檬黄' },
                      { color: '#a5d6a7', label: '薄荷绿' },
                      { color: '#90caf9', label: '冰川蓝' },
                      { color: '#f8bbd0', label: '樱花粉' },
                      { color: '#e0e0e0', label: '优雅灰' },
                    ].map((item) => (
                      <button
                        key={item.color}
                        onClick={() => { editor.chain().focus().toggleMark('highlight', { color: item.color }).run(); setActiveSubMenu('none'); }}
                        className="rounded-full border border-black/10 transition-all hover:scale-110 flex items-center justify-center"
                        style={{ backgroundColor: item.color, width: '22px', height: '22px' }}
                        title={item.label}
                      >
                        {editor.isActive('highlight', { color: item.color }) && <span className="text-[9px] text-black/60 font-bold">✓</span>}
                      </button>
                    ))}
                  </div>
                  {editor.isActive('highlight') && (
                    <button
                      onClick={() => { editor.chain().focus().unsetMark('highlight').run(); setActiveSubMenu('none'); }}
                      className="mt-2 w-full text-center py-1 bg-slate-100 hover:bg-red-50 hover:text-red-500 rounded-md text-[10px] text-slate-500 font-medium transition-colors"
                    >
                      清除高亮
                    </button>
                  )}
                </div>
              )}
            </div>
            <Sep />

            {/* 代码 & 链接 */}
            <TBtn active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()} title="行内代码">
              <Code size={14} />
            </TBtn>
            <TBtn active={editor.isActive('link')} onClick={handleInsertLink} title="超链接">
              <Link2 size={14} />
            </TBtn>
          </div>
        </BubbleMenu>
      )}

      {/* 代码块复制按钮 */}
      {hoveredPre && (
        <button
          style={copyBtnStyle}
          onClick={() => handleCopyCode(hoveredPre)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 bg-slate-700/90 text-white rounded-lg text-xs font-medium hover:bg-slate-700 transition-colors backdrop-blur-sm shadow-md"
        >
          {copiedPre === hoveredPre ? (<><Check size={12} className="text-green-400" /><span className="text-green-400">已复制</span></>) : (<><Copy size={12} /><span>复制</span></>)}
        </button>
      )}

      {/* 行首加号按钮 */}
      {!readOnly && showPlus && (
        <button
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => {
            if (editor && editorRef.current) {
              const editorRect = editorRef.current.getBoundingClientRect();
              const btnAbsTop = editorRect.top + plusPos.top;
              const viewportH = window.innerHeight;
              const menuH = 400;
              const spaceBelow = viewportH - btnAbsTop - 28;
              const menuTop = spaceBelow < menuH ? plusPos.top - menuH + 20 : plusPos.top + 28;
              setPlusMenuPos({ top: menuTop, left: plusPos.left + 36 });
              setShowPlusMenu(!showPlusMenu);
            }
          }}
          className={`editor-plus-btn absolute w-6 h-6 rounded-full flex items-center justify-center bg-white border text-slate-400 shadow-sm transition-all z-20 ${
            showPlusMenu ? 'border-sky-500 text-sky-600 rotate-45' : 'border-slate-200 hover:border-sky-500 hover:text-sky-600'
          }`}
          style={{ top: plusPos.top, left: plusPos.left }}
          title="快捷插入"
        >
          <svg className="w-3.5 h-3.5 text-current transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      )}

      {/* 加号菜单面板 */}
      {showPlusMenu && (
        <div
          className="editor-plus-menu absolute z-50 w-64 bg-white border border-slate-200 rounded-xl shadow-xl p-3 overflow-hidden select-none"
          style={{ top: plusMenuPos.top, left: Math.max(0, plusMenuPos.left) }}
          onMouseDown={(e) => e.preventDefault()}
        >
          <div className="flex items-center justify-between gap-1 pb-2 mb-2 border-b border-slate-100">
            {[1, 2, 3].map((level) => (
              <button key={level} onClick={() => { editor?.chain().focus().clearNodes().toggleHeading({ level: level as 1|2|3 }).run(); setShowPlusMenu(false); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600 font-bold text-xs"
              >H{level}</button>
            ))}
            <div className="w-px h-4 bg-slate-200" />
            <button onClick={() => { editor?.chain().focus().toggleBold().run(); setShowPlusMenu(false); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"><Bold size={13} /></button>
            <button onClick={() => { editor?.chain().focus().toggleItalic().run(); setShowPlusMenu(false); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"><Italic size={13} /></button>
            <button onClick={() => { editor?.chain().focus().toggleBlockquote().run(); setShowPlusMenu(false); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"><Quote size={13} /></button>
            <button onClick={() => { editor?.chain().focus().toggleCode().run(); setShowPlusMenu(false); }} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"><Code size={13} /></button>
          </div>
          <div className="max-h-[260px] overflow-y-auto space-y-0.5">
            <p className="px-2 py-0.5 text-[9px] font-semibold text-slate-400 uppercase tracking-wider">快捷组件</p>
            {[
              { label: '无序列表', desc: '项目符号列表', icon: <List size={14} />, color: 'bg-green-50 text-green-600', action: () => editor?.chain().focus().toggleBulletList().run() },
              { label: '有序列表', desc: '数字序号列表', icon: <ListOrdered size={14} />, color: 'bg-blue-50 text-sky-600', action: () => editor?.chain().focus().toggleOrderedList().run() },
              { label: '代码块', desc: '代码块', icon: <Terminal size={14} />, color: 'bg-slate-100 text-slate-600', action: () => editor?.chain().focus().clearNodes().toggleCodeBlock().run() },
              { label: '数据表格', desc: '可编辑表格', icon: <Table size={14} />, color: 'bg-emerald-50 text-emerald-600', action: () => editor?.chain().focus().clearNodes().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
              { label: '插图', desc: '上传或粘贴图片', icon: <ImageIcon size={14} />, color: 'bg-amber-50 text-amber-500', action: () => { setImageModalOpen(true); } },
              { label: '超链接', desc: '插入链接', icon: <Link2 size={14} />, color: 'bg-sky-50 text-sky-500', action: () => handleInsertLink() },
              { label: '分割线', desc: '水平分割线', icon: <Minus size={14} />, color: 'bg-slate-100 text-slate-400', action: () => editor?.chain().focus().setHorizontalRule().run() },
            ].map((item) => (
              <button key={item.label} onClick={() => { item.action(); setShowPlusMenu(false); }}
                className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-xl hover:bg-slate-50 transition-all text-left text-slate-600"
              >
                <span className={`w-7 h-7 rounded-lg ${item.color} flex items-center justify-center`}>{item.icon}</span>
                <div><div className="text-xs font-semibold">{item.label}</div><div className="text-[10px] text-slate-400">{item.desc}</div></div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 表格操作工具栏 */}
      {editor.isActive('table') && (() => {
        const editorContainer = editorRef.current;
        const tableEl = editorContainer?.querySelector('table');
        let toolbarTop = 0, toolbarLeft = 0;
        if (tableEl && editorContainer) {
          const editorRect = editorContainer.getBoundingClientRect();
          const tableRect = tableEl.getBoundingClientRect();
          toolbarTop = tableRect.top - editorRect.top - 40;
          toolbarLeft = tableRect.left - editorRect.left;
        }
        return (
          <div className="absolute z-40 flex items-center gap-0.5 p-1 bg-white border border-slate-200 rounded-xl shadow-lg select-none"
            style={{ top: toolbarTop, left: toolbarLeft }} onMouseDown={(e) => e.preventDefault()}
          >
            <button onClick={() => editor.chain().focus().addColumnBefore().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 text-[11px]"><ArrowLeftIcon size={12} />插列</button>
            <button onClick={() => editor.chain().focus().addColumnAfter().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 text-[11px]">插列<ArrowRightIcon size={12} /></button>
            <button onClick={() => editor.chain().focus().addRowBefore().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 text-[11px]"><ArrowUpIcon size={12} />插行</button>
            <button onClick={() => editor.chain().focus().addRowAfter().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-slate-100 text-slate-500 text-[11px]">插行<ArrowDownIcon size={12} /></button>
            <div className="w-px h-4 bg-slate-200 mx-0.5" />
            <button onClick={() => editor.chain().focus().deleteColumn().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 text-[11px]"><Trash2 size={11} />删列</button>
            <button onClick={() => editor.chain().focus().deleteRow().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 text-[11px]"><Minus size={11} />删行</button>
            <div className="w-px h-4 bg-slate-200 mx-0.5" />
            <button onClick={() => editor.chain().focus().deleteTable().run()} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 text-[11px]"><X size={11} />删表</button>
          </div>
        );
      })()}

      {/* 飞书式超链接悬浮编辑气泡条 */}
      {linkBubble && (
        <div
          className="link-bubble-bar absolute z-50 flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 rounded-xl shadow-lg select-none animate-in fade-in slide-in-from-bottom-1 duration-150"
          style={{ top: linkBubble.top, left: Math.max(0, linkBubble.left) }}
          onMouseDown={(e) => e.preventDefault()}
          onMouseLeave={() => setLinkBubble(null)}
        >
          {/* URL 预览 */}
          <span className="text-xs text-slate-400 max-w-[200px] truncate font-mono">
            {linkBubble.url.length > 35 ? linkBubble.url.slice(0, 35) + '...' : linkBubble.url}
          </span>
          <div className="w-px h-4 bg-slate-200" />
          {/* 编辑链接 */}
          <button
            onClick={() => {
              const el = currentLinkEl.current;
              if (el && editor) {
                try {
                  const pos = editor.view.posAtDOM(el, 0);
                  const textLen = el.textContent?.length || 0;
                  setLinkEditRange({ from: pos, to: pos + textLen });
                } catch {
                  setLinkEditRange(null);
                }
              }
              setLinkUrlInput(linkBubble.url);
              setLinkTextInput(linkBubble.text);
              setLinkModalOpen(true);
              setLinkBubble(null);
            }}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-500 hover:text-sky-600 transition-colors"
            title="编辑链接"
          >
            <Pencil size={13} />
          </button>
          {/* 取消链接 */}
          <button
            onClick={() => {
              const el = currentLinkEl.current;
              if (el && editor) {
                try {
                  const pos = editor.view.posAtDOM(el, 0);
                  const textLen = el.textContent?.length || 0;
                  editor.chain().focus().setTextSelection({ from: pos, to: pos + textLen }).unsetLink().run();
                } catch {
                  editor?.chain().focus().unsetLink().run();
                }
              } else {
                editor?.chain().focus().unsetLink().run();
              }
              setLinkBubble(null);
            }}
            className="p-1 rounded-md hover:bg-red-50 text-slate-500 hover:text-red-500 transition-colors"
            title="取消链接"
          >
            <Unlink size={13} />
          </button>
          {/* 新窗口打开 */}
          <button
            onClick={() => {
              window.open(linkBubble.url, '_blank', 'noopener,noreferrer');
            }}
            className="p-1 rounded-md hover:bg-slate-100 text-slate-500 hover:text-sky-600 transition-colors"
            title="新窗口打开"
          >
            <ExternalLink size={13} />
          </button>
        </div>
      )}

      {/* 编辑器内容 */}
      <div className="prose prose-sm max-w-none
        prose-headings:text-slate-700 prose-headings:font-semibold
        prose-p:text-slate-600 prose-p:leading-relaxed
        prose-a:text-sky-600 prose-a:underline
        prose-code:text-sky-600 prose-code:bg-slate-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-md prose-code:text-sm prose-code:before:content-[''] prose-code:after:content-['']
        prose-pre:bg-[#1e1e1e] prose-pre:rounded-xl prose-pre:text-sm
        prose-img:rounded-xl prose-img:cursor-pointer
        prose-blockquote:border-sky-400/50 prose-blockquote:text-slate-400
        prose-li:text-slate-600
        prose-strong:text-slate-700
        prose-hr:border-slate-200
      ">
        {/* 有序列表自定义样式：用 ::marker + counter(list-item) — 所有现代浏览器都支持 */}
        <style dangerouslySetInnerHTML={{__html: `
          ol[data-list-style="decimal-paren"] > li::marker {
            content: "(" counter(list-item) ") ";
          }
          ol[data-list-style="decimal-bracket"] > li::marker {
            content: "[" counter(list-item) "] ";
          }
          ol[data-list-style="upper-alpha"] {
            list-style-type: upper-alpha !important;
          }
          ol[data-list-style="lower-alpha"] {
            list-style-type: lower-alpha !important;
          }
          ol[data-list-style="lower-roman"] {
            list-style-type: lower-roman !important;
          }
          ol[data-list-style="circled-decimal"] > li::marker {
            content: "①";
          }
          ol[data-list-style="circled-decimal"] > li:nth-child(1)::marker { content: "① "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(2)::marker { content: "② "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(3)::marker { content: "③ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(4)::marker { content: "④ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(5)::marker { content: "⑤ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(6)::marker { content: "⑥ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(7)::marker { content: "⑦ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(8)::marker { content: "⑧ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(9)::marker { content: "⑨ "; }
          ol[data-list-style="circled-decimal"] > li:nth-child(10)::marker { content: "⑩ "; }
        `}} />
        <EditorContent editor={editor} />
      </div>

      {/* 链接弹窗 */}
      {linkModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-5 shadow-xl relative animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Link2 size={16} className="text-sky-500" />
              {linkEditRange ? '编辑超链接' : '插入超链接'}
            </h3>
            <button onClick={closeLinkModal}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"><X size={16} /></button>
            {(linkEditRange || editor.state.selection.empty) && (
              <input type="text" value={linkTextInput} onChange={(e) => setLinkTextInput(e.target.value)}
                placeholder="链接文本" className="w-full px-3 py-2 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 mb-3" autoFocus />
            )}
            <input type="url" value={linkUrlInput} onChange={(e) => setLinkUrlInput(e.target.value)}
              placeholder="https://example.com" className="w-full px-3 py-2 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200 mb-4"
              autoFocus={linkEditRange ? false : !editor.state.selection.empty}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyLink();
                }
                if (e.key === 'Escape') {
                  closeLinkModal();
                }
              }}
            />
            <div className="flex justify-end gap-2 text-xs font-medium">
              <button onClick={closeLinkModal}
                className="px-3.5 py-2 bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all">取消</button>
              <button onClick={applyLink}
                className="px-3.5 py-2 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-all">确认</button>
            </div>
          </div>
        </div>
      )}

      {/* 图片弹窗 */}
      {imageModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm">
          <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-5 shadow-xl relative">
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <ImageIcon size={16} className="text-amber-500" />
              插入图片
            </h3>
            <button onClick={() => setImageModalOpen(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"><X size={16} /></button>
            <div className="mb-4">
              <button onClick={() => { setImageModalOpen(false); triggerImageUpload(); }}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 hover:border-sky-300 text-slate-500 rounded-xl text-xs font-semibold hover:bg-slate-50 transition-all cursor-pointer mb-3">
                <Upload size={14} /> 上传本地图片
              </button>
              <div className="text-center text-[10px] text-slate-300 uppercase tracking-wider mb-3">或输入网络图片 URL</div>
              <input type="url" value={imageUrlInput} onChange={(e) => setImageUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 bg-slate-100 rounded-xl text-sm outline-none focus:ring-2 focus:ring-sky-200"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (imageUrlInput.trim()) editor.chain().focus().setImage({ src: imageUrlInput.trim() }).run();
                    setImageModalOpen(false); setImageUrlInput('');
                  }
                }}
              />
            </div>
            <div className="flex justify-end gap-2 text-xs font-medium">
              <button onClick={() => { setImageModalOpen(false); setImageUrlInput(''); }}
                className="px-3.5 py-2 bg-slate-100 text-slate-500 rounded-full hover:bg-slate-200 transition-all">取消</button>
              <button onClick={() => {
                  if (imageUrlInput.trim()) editor.chain().focus().setImage({ src: imageUrlInput.trim() }).run();
                  setImageModalOpen(false); setImageUrlInput('');
                }}
                className="px-3.5 py-2 bg-sky-500 text-white rounded-full hover:bg-sky-600 transition-all">确认</button>
            </div>
          </div>
        </div>
      )}

      {/* 底部提示 */}
      {!readOnly && (
        <div className="mt-4 pt-3 border-t border-slate-100 text-xs text-slate-300 flex items-center gap-4">
          <span>输入 <kbd className="px-1.5 py-0.5 bg-slate-100 rounded text-[11px]">/</kbd> 调出命令菜单</span>
          <span>直接粘贴或拖拽图片</span>
          <span>⌘B 加粗 · ⌘I 斜体</span>
        </div>
      )}
    </div>
  );
}
